import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import GlobalAudioControls from '../../components/ui/GlobalAudioControls';
import ConnectionStatusIndicator from '../../components/ui/ConnectionStatusIndicator';
import QuickActionFloatingMenu from '../../components/ui/QuickActionFloatingMenu';
import CurrentToneCard from './components/CurrentToneCard';
import RecentTonesTimeline from './components/RecentTonesTimeline';
import MeetSomeoneCard from './components/MeetSomeoneCard';
import QuickActionsGrid from './components/QuickActionsGrid';
import PlanetaryUpdatesCard from './components/PlanetaryUpdatesCard';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const Dashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [userName, setUserName] = useState('Alex');
  const [currentGreeting, setCurrentGreeting] = useState('');

  useEffect(() => {
    // Simulate loading astrological data
    const loadingTimer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    // Set greeting based on time of day
    const hour = new Date().getHours();
    if (hour < 12) {
      setCurrentGreeting('Good morning');
    } else if (hour < 17) {
      setCurrentGreeting('Good afternoon');
    } else {
      setCurrentGreeting('Good evening');
    }

    return () => clearTimeout(loadingTimer);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center animate-cosmic-glow mb-4 mx-auto">
            <Icon name="Sparkles" size={32} color="white" />
          </div>
          <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">
            Calculating Your Cosmic Tone
          </h2>
          <p className="text-sm text-text-secondary font-mono">
            Aligning planetary positions with musical harmony...
          </p>
          <div className="mt-4 w-48 h-1 bg-surface rounded-full overflow-hidden mx-auto">
            <div className="h-full bg-gradient-to-r from-primary to-accent animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Main Content */}
      <main className="pt-16 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Welcome Section */}
          <div className="py-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-heading font-bold text-text-primary">
                  {currentGreeting}, {userName}
                </h1>
                <p className="text-lg text-text-secondary mt-2">
                  Your cosmic symphony awaits
                </p>
              </div>
              <div className="hidden lg:block">
                <ConnectionStatusIndicator />
              </div>
            </div>

            {/* Astrological Insight Banner */}
            <div className="glass bg-gradient-to-r from-primary/20 to-secondary/20 backdrop-blur-cosmic rounded-2xl p-6 mb-8">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center animate-gentle-pulse">
                  <Icon name="Sparkles" size={24} color="white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-heading font-semibold text-text-primary">
                    Today's Cosmic Insight
                  </h3>
                  <p className="text-sm text-text-secondary mt-1">
                    Venus conjunct Mars in your 5th house creates a harmonious A♭ Major tone, perfect for creative expression and romantic connections.
                  </p>
                </div>
                <Button
                  variant="ghost"
                  iconName="Play"
                  onClick={() => console.log('Play insight tone')}
                  className="text-accent hover:text-accent/80"
                >
                  Listen
                </Button>
              </div>
            </div>
          </div>

          {/* Dashboard Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-8">
              {/* Current Tone - Hero Section */}
              <CurrentToneCard />
              
              {/* Recent Tones Timeline */}
              <RecentTonesTimeline />
            </div>

            {/* Right Column */}
            <div className="space-y-8">
              {/* Meet Someone New */}
              <MeetSomeoneCard />
              
              {/* Quick Actions */}
              <QuickActionsGrid />
              
              {/* Planetary Updates */}
              <PlanetaryUpdatesCard />
            </div>
          </div>

          {/* Mobile-only Connection Status */}
          <div className="lg:hidden mt-8 flex justify-center">
            <ConnectionStatusIndicator />
          </div>
        </div>
      </main>

      {/* Global Components */}
      <GlobalAudioControls />
      <QuickActionFloatingMenu />
    </div>
  );
};

export default Dashboard;